/**
 * W35-LAYERS-LAB — the Layers Lab index table and the per-game
 * summary card.
 *
 * Index: every stack game as one row — game, date, vendor chip,
 * measured video quality, seven layer status chips (L1..L6 + Serve)
 * and the halftime chip. Sortable by game/date/vendor/status,
 * red-anywhere filter, row click opens the per-game view. Every
 * color carries a tooltip naming what is missing; the colors come
 * from the server, never computed here.
 *
 * Summary card: the same row for ONE game, pinned above the per-game
 * layer panels so every layer color and the headline stats are
 * visible before any scrolling. Chip slots are reserved while
 * loading so the card does not jump when data resolves.
 */
import { Settings } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { ApiError, api } from '../lib/api'
import MatchSettingsDialog from '../components/MatchSettingsDialog'
import { CLUB_PARAM } from '../lib/orgAlign'
import { pathSeg } from '../lib/urls'
import { useActionLog } from './actionLog'
import { Elapsed, useBuildRuns, waitingFor } from './buildRuns'
import {
  CHIP_COLORS, CHIP_LABELS, LAYER_KEYS, LAYER_LABELS, chipTooltip,
  halftimeTooltip, timingsIssue, layerStatLine, missingsSummary, summaryItems,
  greenCount, greenThroughL2, relativeTime, servedRank, worstRank,
  MATCH_STATE_LABELS, attentionReasons, matchState, stateRank, type MatchState,
  reviewState, reviewTooltip, type ReviewState,
} from './layersLabModel'
import type { ChipStatus, HalftimeFlag, LayerKey, MatchRow } from './layersLabModel'

const CHIP_STYLE: React.CSSProperties = {
  display: 'inline-block',
  // narrow: seven of them share a column (Ben, 2026-10-17)
  minWidth: '22px',
  padding: '1px 3px',
  borderRadius: '3px',
  fontSize: '11px',
  fontWeight: 600,
  textAlign: 'center',
  color: '#fff',
  cursor: 'default',
}

export function StatusChip({ label, status, title }: {
  label: string
  status: ChipStatus | null
  title: string
}) {
  return (
    <span
      style={{
        ...CHIP_STYLE,
        background: status ? CHIP_COLORS[status] : '#9e9e9e',
      }}
      title={title}
      aria-label={title}
    >
      {label}
    </span>
  )
}

/** "Insights (X/Y, Z)": the game in Manage Insights. X/Y: packs built at
 *  their newest registered version over the pack types registered; Z,
 *  only when some: packs built only at an older version, which a re-run
 *  would refresh (GET .../insights-count). Manage Insights moves into
 *  the game's club itself (alignToGame). The count is decoration: if it
 *  cannot be read the button still opens the page. */
type InsightsCount = { succeeded: number; total: number; stale?: number }

export function InsightsButton({ gameId, gameCode }: { gameId: number; gameCode: string }) {
  const [count, setCount] = useState<InsightsCount | null>(null)
  useEffect(() => {
    let on = true
    setCount(null)
    api<InsightsCount>(`/api/v1/opsqa/games/${gameId}/insights-count`)
      .then((d) => { if (on) setCount(d) })
      .catch(() => { /* the button still opens the page */ })
    return () => { on = false }
  }, [gameId])
  const stale = count?.stale ?? 0
  return (
    <a className="adm-btn adm-btn--quiet" style={{ textDecoration: 'none' }}
       href={`/manage-insights/game/${pathSeg(gameCode)}`}
       title={count ? `${count.succeeded} of ${count.total} insight packs built at their `
                      + `latest version${stale ? `; ${stale} built at an older version only` : ''}`
                    : 'This game in Manage Insights'}>
      Insights{count ? ` (${count.succeeded}/${count.total}${stale ? `, ${stale}` : ''})` : ''}
    </a>
  )
}

/** "AWAY @ HOME" with each team's crest beside it where there is one;
 *  the hidden date token is left off (it is in the Date column). A crest
 *  that fails to load drops out rather than show a broken image. */
export function GameTeams({ code, away, home }: {
  code: string; away?: string | null; home?: string | null
}) {
  const [a = '', h = ''] = code.split('@')
  const crest = (src: string | null | undefined) => src ? (
    <img src={src} alt="" className="ls-crest" loading="lazy"
         onError={(e) => { e.currentTarget.style.display = 'none' }} />
  ) : null
  return (
    <span className="ls-game-teams" title={code}>
      {crest(away)}<span>{a.trim()}</span>
      <span className="muted">@</span>
      <span>{h.trim()}</span>{crest(home)}
    </span>
  )
}

/** A green check while the match periods are fine; otherwise the chip's
 *  colour with what is wrong, in a few words (timingsIssue). */
export function TimingsCell({ flag }: { flag: HalftimeFlag }) {
  const issue = timingsIssue(flag)
  const title = halftimeTooltip(flag)
  if (issue == null) {
    return (
      <span role="img" aria-label={`Timings OK. ${title}`} title={title}
            style={{ color: CHIP_COLORS.green, fontWeight: 700, fontSize: '14px' }}>
        &#x2713;
      </span>
    )
  }
  return (
    <span title={title} aria-label={`Timings: ${issue}. ${title}`}
          style={{ ...CHIP_STYLE, background: CHIP_COLORS[flag.status],
                   whiteSpace: 'nowrap' }}>
      {issue}
    </span>
  )
}

/** The seven layer chips + halftime, one row. `row` null renders
 *  gray placeholders in the same slots — no layout jump. */
export function ChipRow({ row }: { row: MatchRow | null }) {
  return (
    <span style={{ display: 'inline-flex', gap: '4px',
                   flexWrap: 'wrap' }}>
      {LAYER_KEYS.map((k: LayerKey) => (
        <StatusChip
          key={k}
          label={CHIP_LABELS[k]}
          status={row ? row.layers[k].status : null}
          title={row ? chipTooltip(k, row.layers[k]) : 'loading…'}
        />
      ))}
      <StatusChip
        label="Timings"
        status={row ? row.halftime.status : null}
        title={row ? halftimeTooltip(row.halftime) : 'loading…'}
      />
    </span>
  )
}

type MatchesPayload = {
  matches: MatchRow[]; count: number
  /** Builds may overwrite built layers here (dev and local). */
  overwrite_allowed?: boolean
}

/** Force-rebuild L3 and everything above it, for a row green through L2.
 *  The server re-checks the chips and the environment; its answer —
 *  queued or why not — is shown in place of the button. */
function RebuildFromL3({ row }: { row: MatchRow }) {
  const [state, setState] = useState<'idle' | 'busy' | 'queued' | 'error'>('idle')
  const [msg, setMsg] = useState<string | null>(null)
  if (state === 'queued' || state === 'error') {
    return (
      <span style={{ fontSize: '10px',
                     color: state === 'error' ? '#ef4444' : '#8b8b8b' }}>
        {msg}
      </span>
    )
  }
  return (
    <button className="adm-btn" style={{ fontSize: '10px', padding: '1px 6px' }}
            disabled={state === 'busy'}
            title="Force-rebuild L3 and every layer above it, overwriting what is built"
            onKeyDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation()       // the row opens the match
              if (!confirm(`Force-rebuild L3 and everything above it for game `
                + `#${row.game_id}? Built layers are overwritten.`)) return
              setState('busy')
              api<{ batch_id: string }>(
                `/api/v1/opsqa/games/${row.game_id}/rebuild-from-l3`,
                { method: 'POST' })
                .then((d) => {
                  setState('queued')
                  setMsg(`Rebuild from L3 queued (${d.batch_id.slice(0, 8)})`)
                })
                .catch((err: unknown) => {
                  setState('error')
                  setMsg(err instanceof ApiError ? err.message : String(err))
                })
            }}>
      Rebuild L3+
    </button>
  )
}

const STATE_COLORS: Record<MatchState, string> = {
  complete: CHIP_COLORS.green, attention: CHIP_COLORS.red, convert: '#888',
}

/** The match's state in one word, and why in the tooltip. */
function StateBadge({ row }: { row: MatchRow }) {
  const st = matchState(row)
  const why = attentionReasons(row)
  return (
    <span data-testid={`state-${row.game_id}`}
          title={st === 'attention' ? why.join(' · ')
            : st === 'convert' ? 'No tracking ingested to the stack yet'
              : 'All layers green, no failed build step, served from the stack'}
          style={{ fontSize: '10px', fontWeight: 600, whiteSpace: 'nowrap',
                   textTransform: 'uppercase', color: STATE_COLORS[st] }}>
      {MATCH_STATE_LABELS[st]}
      {row.failed_steps?.length
        ? <span style={{ fontWeight: 400, textTransform: 'none' }}>
            {' '}· {row.failed_steps.length} failed</span> : null}
    </span>
  )
}

type SortKey = 'served' | 'state' | 'game' | 'club' | 'date' | 'vendor' | 'status'
  | 'serve_built' | 'reviewed'

const REVIEW_COLORS: Record<ReviewState, string> = {
  reviewed: CHIP_COLORS.green, not_reviewed: CHIP_COLORS.red, unknown: '#999',
}
const REVIEW_RANK: Record<ReviewState, number> = {
  reviewed: 2, not_reviewed: 1, unknown: 0,
}

const REVIEW_LABELS: Record<ReviewState, string> = {
  reviewed: 'Yes', not_reviewed: 'No', unknown: '\u2014',
}

/** Match Lab's Human reviewed sign-off in a word: green Yes, red No,
 *  a grey dash where it could not be read (Ben, 2026-10-22). */
function ReviewFlag({ row }: { row: MatchRow }) {
  const st = reviewState(row)
  const tip = reviewTooltip(row)
  return (
    <span aria-label={tip} title={tip}
          data-testid={`reviewed-${row.game_id}`} data-state={st}
          style={{ fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap',
                   verticalAlign: 'middle', color: REVIEW_COLORS[st] }}>
      {REVIEW_LABELS[st]}
    </span>
  )
}

export function LayersLabIndex({ onOpen, club, onClub, canSettings = false,
                                canUploadBadges = false, canDeleteMatches = false }: {
  onOpen: (gameId: number) => void
  /** Show the per-match gear (MatchSettingsDialog): staff. */
  canSettings?: boolean
  /** Badge uploads in that dialog: platform admins. */
  canUploadBadges?: boolean
  /** Platform admins: Delete match in the settings dialog. */
  canDeleteMatches?: boolean
  // The page's ?club=<organization id>: one filter shared with the Layer
  // Stack view, so a link or a view switch keeps the club.
  club: string
  onClub: (club: string | null) => void
}) {
  const [rows, setRows] = useState<MatchRow[] | null>(null)
  const [settingsFor, setSettingsFor] = useState<MatchRow | null>(null)
  const [overwriteAllowed, setOverwriteAllowed] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Green dot first by default (Ben, 2026-09-30): the matches fully on
  // the stack, newest first within each group.
  const [sortBy, setSortBy] = useState<SortKey>('served')
  const [desc, setDesc] = useState(true)
  // The row's worst chip: '' all, 'red' any red, 'yellow' stale — a
  // yellow chip and no red, so the yellow rows are seen apart from the red
  // (Ben, 2026-10-30).
  const [chipFilter, setChipFilter] = useState<'' | 'red' | 'yellow'>('')
  const [stackOnly, setStackOnly] = useState(false)
  // Red matches customers are being served (the server's red_and_served).
  const [redServedOnly, setRedServedOnly] = useState(false)
  // Where each match stands (layersLabModel.matchState); '' is all.
  const [stateFilter, setStateFilter] = useState<MatchState | ''>('')
  // Human reviewed or not (Match Lab's sign-off); '' is all.
  const [reviewFilter, setReviewFilter] = useState<'reviewed' | 'not_reviewed' | ''>('')
  // Games deleted from Manage Games keep their stack rows; hidden unless
  // asked for, so staff can still find (and clean up) them.
  const [showDeleted, setShowDeleted] = useState(false)

  // read again after a settings save, so the row shows what was saved
  const [reloadKey, setReloadKey] = useState(0)
  useEffect(() => {
    api<MatchesPayload>('/api/v1/opsqa/matches')
      .then((d) => {
        setRows(d.matches)
        setOverwriteAllowed(d.overwrite_allowed === true)
      })
      .catch((e: unknown) =>
        setError(e instanceof ApiError ? e.message : String(e)))
  }, [reloadKey])

  // Derive unique clubs from the data
  const clubs = useMemo(() => {
    if (!rows) return []
    const seen = new Map<number, string>()
    for (const r of rows) {
      if (r.club?.id != null && r.club.name) {
        seen.set(r.club.id, r.club.name)
      }
    }
    return [...seen.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [rows])

  if (error) return <p className="error">{error}</p>
  if (rows === null) return <p className="muted">Loading matches…</p>
  if (rows.length === 0) {
    return <p className="muted">No games in layer stack yet.</p>
  }

  const deletedCount = rows.filter((r) => r.deleted).length
  const live = showDeleted ? rows : rows.filter((r) => !r.deleted)
  const afterClub = club
    ? live.filter((r) => String(r.club?.id) === club)
    : live
  // Counted before every other filter but club, so it never hides.
  const redServedCount = afterClub.filter((r) => r.red_and_served).length
  const afterRedServed = redServedOnly
    ? afterClub.filter((r) => r.red_and_served) : afterClub
  // Counted before the chip filter, so each option says what it holds.
  const chipCounts = { red: 0, yellow: 0 }
  for (const r of afterRedServed) {
    const w = worstRank(r)
    if (w === 0) chipCounts.red += 1
    else if (w === 1) chipCounts.yellow += 1
  }
  const afterRed = chipFilter
    ? afterRedServed.filter((r) => worstRank(r) === (chipFilter === 'red' ? 0 : 1))
    : afterRedServed
  const afterServed = stackOnly
    ? afterRed.filter((r) => servedRank(r) === 1) : afterRed
  // Counts before the review filter, so each option says what it holds.
  const reviewCounts = { reviewed: 0, not_reviewed: 0, unknown: 0 }
  for (const r of afterServed) reviewCounts[reviewState(r)] += 1
  const afterStack = reviewFilter
    ? afterServed.filter((r) => reviewState(r) === reviewFilter) : afterServed
  // Counts before the state filter, so each button says what it holds.
  const stateCounts = { convert: 0, attention: 0, complete: 0 }
  for (const r of afterStack) stateCounts[matchState(r)] += 1
  const shown = stateFilter
    ? afterStack.filter((r) => matchState(r) === stateFilter) : afterStack
  const sorted = [...shown].sort((a, b) => {
    let cmp = 0
    if (sortBy === 'game') cmp = a.game_id - b.game_id
    else if (sortBy === 'club') {
      cmp = (a.club?.name ?? '').localeCompare(b.club?.name ?? '')
    } else if (sortBy === 'date') {
      cmp = (a.match_date ?? '').localeCompare(b.match_date ?? '')
    } else if (sortBy === 'vendor') {
      cmp = a.vendor.localeCompare(b.vendor)
    } else if (sortBy === 'served') {
      cmp = servedRank(a) - servedRank(b)
    } else if (sortBy === 'state') {
      cmp = stateRank(a) - stateRank(b)
    } else if (sortBy === 'reviewed') {
      cmp = REVIEW_RANK[reviewState(a)] - REVIEW_RANK[reviewState(b)]
    } else if (sortBy === 'serve_built') {
      cmp = (a.serve_built_at ?? '').localeCompare(b.serve_built_at ?? '')
    } else {
      // most green chips, then the least-bad worst chip
      cmp = greenCount(a) - greenCount(b)
      if (cmp === 0) cmp = worstRank(a) - worstRank(b)
    }
    if (cmp !== 0) return desc ? -cmp : cmp
    // ties: newest match first, then the higher game number — whichever
    // way the primary key runs
    return (b.match_date ?? '').localeCompare(a.match_date ?? '')
      || b.game_id - a.game_id
  })

  const sortHeader = (key: SortKey, label: string) => (
    <th
      className={`is-clickable${key === 'club' ? ' ls-col-club' : ''}`}
      role="button"
      tabIndex={0}
      onClick={() => {
        if (sortBy === key) setDesc(!desc)
        else { setSortBy(key); setDesc(key !== 'club' && key !== 'state') }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          if (sortBy === key) setDesc(!desc)
          else { setSortBy(key); setDesc(key !== 'club' && key !== 'state') }
        }
      }}
      title={key === 'served' ? 'Sort by stack data (green dot first)'
        : key === 'status' ? 'Sort by layers (most green first)'
          : key === 'state' ? 'Sort by status (needs work first)'
            : key === 'serve_built' ? 'Sort by when the serve layer last changed'
            : key === 'reviewed' ? 'Sort by human reviewed (reviewed first)'
          : `Sort by ${label.toLowerCase()}`}
    >
      {label}{sortBy === key ? (desc ? ' ↓' : ' ↑') : ''}
    </th>
  )

  return (
    <>
      <div className="adm-bar" style={{ gap: 'var(--space-2)' }}
           role="group" aria-label="Match status">
        {(['', 'attention', 'convert', 'complete'] as const).map((st) => (
          <button key={st || 'all'} type="button"
                  className={`adm-btn${stateFilter === st ? ' is-active' : ''}`}
                  aria-pressed={stateFilter === st}
                  style={{ fontSize: '12px', padding: '2px 8px',
                           fontWeight: stateFilter === st ? 700 : 400 }}
                  onClick={() => setStateFilter(st)}>
            {st ? `${MATCH_STATE_LABELS[st]} (${stateCounts[st]})`
              : `All (${afterStack.length})`}
          </button>
        ))}
        <button type="button" data-testid="red-served-filter"
                className={`adm-btn ls-red-served${redServedCount ? ' has-some' : ''}${redServedOnly ? ' is-active' : ''}`}
                aria-pressed={redServedOnly}
                title="Matches customers are being served while a build step is failed"
                onClick={() => setRedServedOnly(!redServedOnly)}>
          Red and served ({redServedCount})
        </button>
      </div>
      <div className="adm-bar" style={{ gap: 'var(--space-2)' }}>
        {(clubs.length > 1 || club) && (
          <select className="adm-select"
                  value={club}
                  style={{ minWidth: '140px', fontSize: '12px' }}
                  onChange={(e) => onClub(e.target.value || null)}>
            <option value="">All clubs</option>
            {/* A club in the URL with no stack games still shows as
                chosen, not as "All clubs" over an empty table. */}
            {club && !clubs.some((c) => String(c.id) === club)
              && <option value={club}>Club #{club}</option>}
            {clubs.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        )}
        <select className="adm-select" aria-label="Chips"
                value={chipFilter}
                onChange={(e) => setChipFilter(e.target.value as '' | 'red' | 'yellow')}
                title="By the row's worst chip: any red, or stale — yellow with no red">
          <option value="">All chips</option>
          <option value="red">Red anywhere ({chipCounts.red})</option>
          <option value="yellow">Yellow, no red ({chipCounts.yellow})</option>
        </select>
        <label style={{ display: 'inline-flex', gap: '4px',
                        alignItems: 'center', fontSize: '12px' }}
               title="Only matches fully on the stack: switched on and video certified">
          <input
            type="checkbox"
            checked={stackOnly}
            onChange={(e) => setStackOnly(e.target.checked)}
          />
          Green dot only
        </label>
        <select className="adm-select" aria-label="Human reviewed"
                value={reviewFilter}
                style={{ fontSize: '12px' }}
                title="Match Lab's Human reviewed sign-off"
                onChange={(e) => setReviewFilter(
                  e.target.value as 'reviewed' | 'not_reviewed' | '')}>
          <option value="">Human reviewed: all</option>
          <option value="reviewed">Human reviewed ({reviewCounts.reviewed})</option>
          <option value="not_reviewed">Not human reviewed ({reviewCounts.not_reviewed})</option>
        </select>
        {deletedCount > 0 && (
          <label style={{ display: 'inline-flex', gap: '4px',
                          alignItems: 'center', fontSize: '12px' }}
                 title="Games deleted from Manage Games whose layer-stack data remains">
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
            />
            Show deleted ({deletedCount})
          </label>
        )}
        <span className="muted" style={{ fontSize: '12px' }}>
          {sorted.length} of {rows.length} matches
        </span>
      </div>
      {/* On a phone the table scrolls inside its own box — the page
          itself must never scroll sideways (layer-stack.css). */}
      <div className="ls-table-scroll">
      <table className="adm-table">
        <thead>
          <tr>
            {sortHeader('served', '\u25CF')}
            {sortHeader('state', 'Status')}
            {sortHeader('reviewed', 'Reviewed')}
            {sortHeader('game', 'Game')}
            {sortHeader('club', 'Club')}
            {sortHeader('date', 'Date')}
            {sortHeader('vendor', 'Vendor')}
            <th>Source</th>
            <th>Video</th>
            {sortHeader('status', 'Layers')}
            <th>Timings</th>
            {sortHeader('serve_built', 'Serve updated')}
            {canSettings && <th aria-label="Settings" />}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => {
            const missing = missingsSummary(r)
            return (
              <React.Fragment key={r.game_id}>
                <tr
                  className="is-clickable"
                  role="button"
                  tabIndex={0}
                  onClick={() => onOpen(r.game_id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') onOpen(r.game_id)
                  }}
                  style={missing ? { borderBottom: 'none' } : undefined}
                >
                  <td>
                    {r.served_from && (
                      <span
                        role="img"
                        aria-label={r.served_from === 'stack'
                          ? 'Served from stack data' : 'Served from legacy data'}
                        title={r.served_note ?? (r.served_from === 'stack'
                          ? 'Served from stack data'
                          : 'Served from legacy data')}
                        style={{ display: 'inline-block', width: '8px',
                                 height: '8px', borderRadius: '50%',
                                 marginRight: '6px', verticalAlign: 'middle',
                                 background: CHIP_COLORS[
                                   r.served_from === 'stack' ? 'green' : 'yellow'] }}
                      />
                    )}
                  </td>
                  <td>
                    <StateBadge row={r} />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <ReviewFlag row={r} />
                  </td>
                  <td>
                    <span className="t-data">#{r.game_id}</span>
                    {' '}
                    {r.deleted && (
                      <span className="muted" style={{ fontSize: '10px' }}
                            title="Deleted from Manage Games; layer-stack data remains">
                        [deleted]{' '}
                      </span>
                    )}
                    {r.game_code
                      ? <GameTeams code={r.game_code} away={r.away_crest}
                                   home={r.home_crest} />
                      : (r.home_team && r.away_team
                        ? `${r.away_team} @ ${r.home_team}` : '')}
                  </td>
                  <td className="ls-col-club">
                    {r.club ? (
                      <span style={{ display: 'inline-flex', gap: '6px',
                                     alignItems: 'center' }}
                            title={r.club.name ?? r.club.code ?? undefined}>
                        {r.club.logo_url && (
                          <img src={r.club.logo_url} alt=""
                               style={{ height: '16px', width: '16px',
                                        objectFit: 'contain' }} />
                        )}
                        {/* a phone shows the crest alone; the name is the
                            title (layer-stack.css) */}
                        <span className="ls-club-name">
                          {r.club.name ?? r.club.code ?? '—'}
                        </span>
                      </span>
                    ) : '—'}
                  </td>
                  <td className="t-data">{r.match_date ?? '—'}</td>
                  <td>
                    <span title={r.vendor_note ?? undefined}>
                      {r.vendor || (r.pre_layers ? '\u2014' : '?')}
                    </span>
                  </td>
                  <td>
                    {r.pre_layers ? (
                      <span style={{ fontSize: '10px', fontWeight: 600,
                                     color: r.has_r2_tracking ? '#b28704' : '#999',
                                     textTransform: 'uppercase' }}
                            title={r.has_r2_tracking
                              ? 'R2 tracking data available — ready for layer stack processing'
                              : 'No tracking data on R2'}>
                        {r.has_r2_tracking ? 'R2 ready' : 'No data'}
                      </span>
                    ) : (
                      <span style={{ fontSize: '10px', fontWeight: 600,
                                     color: '#1c7c31',
                                     textTransform: 'uppercase' }}>
                        Stack
                      </span>
                    )}
                  </td>
                  <td title={`Basis: ${r.video_quality.basis}`}>
                    {r.video_quality.label}
                  </td>
                  <td>
                    <span style={{ display: 'inline-flex', gap: '4px' }}>
                      {LAYER_KEYS.map((k: LayerKey) => (
                        <StatusChip
                          key={k}
                          label={CHIP_LABELS[k]}
                          status={r.layers[k].status}
                          title={chipTooltip(k, r.layers[k])}
                        />
                      ))}
                      {overwriteAllowed && greenThroughL2(r) && (
                        <RebuildFromL3 row={r} />
                      )}
                    </span>
                  </td>
                  <td>
                    <TimingsCell flag={r.halftime} />
                  </td>
                  <td className="muted" style={{ fontSize: '11px', whiteSpace: 'nowrap' }}
                      data-testid={`serve-built-${r.game_id}`}
                      title={r.serve_built_at
                        ? `Serve layer last built ${r.serve_built_at}`
                        : 'Serve layer never built'}>
                    {relativeTime(r.serve_built_at ?? null) ?? '\u2014'}
                  </td>
                  {canSettings && (
                    <td onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}>
                      {r.game_code && r.club && !r.deleted && (
                        <button type="button" className="ls-gear"
                                aria-label={`Settings ${r.game_code}`}
                                title="Team names, badges, colors, date, direction, timings, pitch"
                                onClick={() => setSettingsFor(r)}>
                          <Settings size={14} aria-hidden />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
                {missing && (
                  <tr
                    className="is-clickable"
                    onClick={() => onOpen(r.game_id)}
                    style={{ borderTop: 'none' }}
                  >
                    <td colSpan={canSettings ? 13 : 12}
                        style={{ paddingTop: 0, paddingBottom: '6px',
                                 fontSize: '11px', color: '#888',
                                 fontStyle: 'normal', lineHeight: 1.3 }}>
                      {missing}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            )
          })}
        </tbody>
      </table>
      </div>
      {settingsFor?.game_code && settingsFor.club && (
        <MatchSettingsDialog orgId={settingsFor.club.id}
                             gameCode={settingsFor.game_code}
                             gameId={settingsFor.game_id}
                             canUploadBadges={canUploadBadges}
                             canDelete={canDeleteMatches}
                             onSaved={() => setReloadKey((n) => n + 1)}
                             onDeleted={() => setReloadKey((n) => n + 1)}
                             onClose={() => setSettingsFor(null)} />
      )}
    </>
  )
}

/** Pinned summary card for the per-game view: seven chips + HT,
 *  vendor, video, per-layer stats, check verdicts and build times —
 *  one glance, no scroll. */
type PipelineStepMin = {
  id: string; label: string; status: string; buildable?: boolean
}

/** "Nothing to build" must not read like "all built" when some checks
 *  could not run (status unknown) — say so. */
function noPendingMsg(steps: PipelineStepMin[]): string {
  const unknown = steps.filter((s) => s.status === 'unknown').length
  return unknown > 0
    ? `No pending steps (${unknown} unknown \u2014 checks could not run)`
    : 'No pending buildable steps'
}

function BuildActions({ gameId }: { gameId: number }) {
  const [steps, setSteps] = useState<PipelineStepMin[] | null>(null)
  const [msg, setMsg] = useState<string | null>(null)
  const runs = useBuildRuns()
  const { stackVersion } = useActionLog()

  const load = useCallback(() => {
    api<{ steps: PipelineStepMin[] }>(
      `/api/v1/opsqa/games/${gameId}/pipeline-status`)
      .then((d) => setSteps(d.steps))
      .catch(() => {})
  }, [gameId])

  useEffect(() => { load() }, [load, stackVersion])

  // Builds are queued through the page's build-run provider; outside
  // one (no poller to report progress) the buttons are not offered.
  if (!steps || !runs) return null
  // Pending and not already queued/running: those would only queue twice.
  const pending = steps.filter(
    (s) => s.status === 'pending' && s.buildable !== false
      && !runs.activeSteps.has(s.id))
  const allBuilt = steps.every((s) => s.status === 'built')
  const busy = runs.activeBatch !== null

  const buildNext = () => {
    const next = pending[0]
    if (!next) { setMsg(noPendingMsg(steps)); return }
    setMsg(null)
    void runs.start([next.id], `next: ${next.label}`)
  }
  const buildAll = () => {
    if (pending.length === 0) { setMsg(noPendingMsg(steps)); return }
    setMsg(null)
    void runs.start(pending.map((s) => s.id), 'all pending')
  }

  return (
    <span style={{ display: 'inline-flex', gap: '4px', alignItems: 'center' }}>
      <button className="adm-btn"
              style={{ fontSize: '10px', padding: '1px 6px' }}
              disabled={allBuilt || pending.length === 0}
              title={allBuilt ? 'All steps built' : undefined}
              onClick={buildNext}>
        Build next
      </button>
      <button className="adm-btn"
              style={{ fontSize: '10px', padding: '1px 6px' }}
              disabled={allBuilt || pending.length === 0}
              title={allBuilt ? 'All steps built' : undefined}
              onClick={buildAll}>
        Build all
      </button>
      {busy && (
        <>
          <span style={{ fontSize: '10px', color: '#eab308' }}>
            {runs.runningStep ? `${runs.runningStep}…` : (() => {
              const q = runs.runs.find((r) => r.batch_id === runs.activeBatch)
              return q ? <>{waitingFor(q.runner)}<Elapsed since={q.queued_at} /></>
                : 'queued…'
            })()}
          </span>
          <button className="adm-btn"
                  style={{ fontSize: '10px', padding: '1px 6px',
                           color: '#ef4444' }}
                  onClick={() => void runs.cancel()}>
            Cancel
          </button>
        </>
      )}
      {msg && !busy && (
        <span style={{ fontSize: '10px', color: '#8b8b8b' }}>{msg}</span>
      )}
    </span>
  )
}

/** The customer's match page for this game, as a plain link — so it opens
 *  in a new tab, copies and middle-clicks like any URL (Ben, 2026-10-26).
 *  That page only shows games in the session's current club, so ?club=
 *  names which club's copy: the match route switches to it through the API
 *  first (App.tsx ClubGate). Never the legacy /game-match-summary page
 *  route, a Flask page that sent anyone it could not authenticate to the
 *  old login (Ben, 2026-10-27). */
export function matchVideoHref(gameCode: string, clubId: number): string {
  return `/match-room/${pathSeg(gameCode)}?${CLUB_PARAM}=${clubId}`
}

function MatchVideoButton({ gameCode, clubId }: { gameCode: string; clubId: number }) {
  return (
    <a className="adm-btn adm-btn--quiet" style={{ textDecoration: 'none' }}
       href={matchVideoHref(gameCode, clubId)}
       title="The match video page, as the club sees it">
      Match Video
    </a>
  )
}

export function StackSummaryCard({ gameId, actionsSlot, settings }: {
  gameId: number
  /** Rendered between the chips and Build next — the match view puts
   *  its Import affordance here while the game has no clean_game row. */
  actionsSlot?: React.ReactNode
  /** The Matches tab's per-match gear, here too (Ben, 2026-10-21): the
   *  same MatchSettingsDialog, for staff. Absent = no gear. */
  settings?: { canUploadBadges: boolean; canDelete?: boolean
               onSaved?: () => void; onDeleted?: () => void }
}) {
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [row, setRow] = useState<MatchRow | null>(null)
  const [state, setState] =
    useState<'loading' | 'ok' | 'absent' | 'error'>('loading')
  const [error, setError] = useState<string | null>(null)
  const { stackVersion } = useActionLog()

  const loadRow = useCallback(() => {
    setRow(null); setState('loading'); setError(null)
    api<MatchesPayload>(`/api/v1/opsqa/matches?game_id=${gameId}`)
      .then((d) => {
        if (d.matches.length === 0) { setState('absent'); return }
        setRow(d.matches[0] ?? null)
        setState('ok')
      })
      .catch((e: unknown) => {
        setState('error')
        setError(e instanceof ApiError ? e.message : String(e))
      })
  }, [gameId])

  useEffect(() => { loadRow() }, [loadRow])
  // Quiet refetch after any successful action on the page (no
  // loading flash): chips and vendor follow ingest and builds.
  useEffect(() => {
    if (stackVersion === 0) return
    api<MatchesPayload>(`/api/v1/opsqa/matches?game_id=${gameId}`)
      .then((d) => { if (d.matches[0]) setRow(d.matches[0]) })
      .catch(() => {})
  }, [stackVersion]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 5,
        background: 'var(--color-surface-raised, #fff)',
        border: '1px solid var(--color-border, #ccc)',
        borderRadius: '4px',
        padding: 'var(--space-2)',
        marginBottom: 'var(--space-3)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', flexWrap: 'wrap',
                    gap: 'var(--space-2)' }}>
        <span style={{ display: 'inline-flex', gap: 'var(--space-2)',
                       alignItems: 'center', flexWrap: 'wrap' }}>
          <span className="t-label">Game #{gameId}</span>
          <ChipRow row={row} />
          {actionsSlot}
          <BuildActions gameId={gameId} />
        </span>
        <span style={{ display: 'inline-flex', gap: 'var(--space-2)' }}>
          {row?.game_code && (
            <InsightsButton gameId={gameId} gameCode={row.game_code} />
          )}
          {row?.game_code && row.club && (
            <MatchVideoButton gameCode={row.game_code} clubId={row.club.id} />
          )}
          <a
            className="adm-btn"
            href={`/ops/match-lab/${gameId}`}
            style={{ textDecoration: 'none' }}
          >
            Match Lab
          </a>
          {settings && row?.game_code && row.club && !row.deleted && (
            <button type="button" className="adm-btn ls-gear"
                    aria-label={`Settings ${row.game_code}`}
                    title="Team names, badges, colors, date, direction, timings, pitch"
                    onClick={() => setSettingsOpen(true)}>
              <Settings size={14} aria-hidden />
            </button>
          )}
        </span>
      </div>
      {settings && settingsOpen && row?.game_code && row.club && (
        <MatchSettingsDialog orgId={row.club.id}
                             gameCode={row.game_code}
                             gameId={row.game_id}
                             canUploadBadges={settings.canUploadBadges}
                             canDelete={settings.canDelete}
                             onSaved={() => { loadRow(); settings.onSaved?.() }}
                             onDeleted={settings.onDeleted}
                             onClose={() => setSettingsOpen(false)} />
      )}
      {state === 'error' && <p className="error">{error}</p>}
      {state === 'absent' && (
        <p className="muted">
          Game {gameId} is not in the stack index (no ingest batches
          and no clean_game row).
        </p>
      )}
      {state === 'ok' && row && (
        <>
          <div style={{ display: 'flex', gap: 'var(--space-3)',
                        flexWrap: 'wrap', marginTop: 'var(--space-1)',
                        fontSize: '12px' }}>
            {summaryItems(row).map((it) => (
              <span key={it.label} title={it.title}>
                <span className="t-label">{it.label}</span>{' '}
                <span className="t-data">{it.value}</span>
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-3)',
                        flexWrap: 'wrap', marginTop: '4px',
                        fontSize: '11px' }}
               className="muted">
            {LAYER_KEYS.map((k: LayerKey) => (
              <span key={k} title={chipTooltip(k, row.layers[k])}>
                {LAYER_LABELS[k]}: {layerStatLine(k, row.layers[k])}
              </span>
            ))}
          </div>
        </>
      )}
    </section>
  )
}
